import { AlertTriangle, Calculator } from "lucide-react";
import { PageShell } from "../components/PageShell";
import { PageHero } from "../components/PageHero";
import { YieldDisclaimer } from "../components/YieldDisclaimer";
import { RISKS, YIELD_CALC } from "../data/content";

export default function InvestmentGuide() {
  return (
    <PageShell>
      <PageHero
        eyebrow="Investment Guide"
        title="Understanding Dubai property"
        italic="the honest way"
        subtitle="A transparent walk-through of how gross rental yield is calculated, the risks every investor should factor in, and what a realistic net return looks like."
      />

      {/* Yield calculation */}
      <section className="py-16 md:py-24 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-[10px] tracking-[0.5em] text-[#c5a059] uppercase mb-4">01 · Yield Calculation</div>
          <h2 className="font-serif-display text-3xl md:text-5xl font-light mb-8">
            How <em className="italic gold-gradient">rental yield</em> is calculated
          </h2>

          <div className="glass-strong rounded-2xl p-8 md:p-10 mb-6" data-testid="yield-formula">
            <div className="flex items-center gap-3 mb-5 text-[#c5a059]">
              <Calculator size={18} />
              <span className="text-[10px] tracking-[0.4em] uppercase">The Formula</span>
            </div>
            <div className="font-serif-display text-xl md:text-2xl leading-relaxed text-white">
              Estimated Gross Rental Yield ={" "}
              <span className="gold-gradient italic">
                (Annual Rental Income ÷ Property Purchase Price) × 100
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="glass rounded-xl p-6">
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2">Property price</div>
              <div className="font-serif-display text-2xl text-[#c5a059]">{YIELD_CALC.example.price}</div>
            </div>
            <div className="glass rounded-xl p-6">
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2">Annual rent</div>
              <div className="font-serif-display text-2xl text-[#c5a059]">{YIELD_CALC.example.annualRent}</div>
            </div>
            <div className="glass rounded-xl p-6">
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2">Gross yield</div>
              <div className="font-serif-display text-2xl text-green-400">{YIELD_CALC.example.result}</div>
            </div>
          </div>

          <div className="rounded-xl p-5 border border-[#c5a059]/25 bg-[#c5a059]/5">
            <p className="text-sm text-white/75 leading-relaxed">
              <span className="text-[#c5a059] font-semibold">Important:</span> {YIELD_CALC.example.net}
            </p>
          </div>

          <YieldDisclaimer className="mt-6" />
        </div>
      </section>

      {/* Risks */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" style={{ background: "rgba(197,160,89,0.03)" }}>
        <div className="max-w-6xl mx-auto">
          <div className="flex items-start gap-3 mb-6">
            <AlertTriangle size={18} className="text-[#c5a059] mt-1" />
            <div className="text-[10px] tracking-[0.5em] text-[#c5a059] uppercase">02 · Investment Risks</div>
          </div>
          <h2 className="font-serif-display text-3xl md:text-5xl font-light mb-4 max-w-3xl">
            What can affect your <em className="italic gold-gradient">actual return</em>
          </h2>
          <p className="text-white/60 max-w-2xl mb-14 leading-relaxed">
            Property is not a risk-free asset. Below is an honest summary of the factors every
            investor should weigh before committing capital to a Dubai property purchase.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="risks-grid">
            {RISKS.map((r, i) => (
              <div key={r.title} className="glass rounded-xl p-6" data-testid={`risk-${i}`}>
                <div className="text-[10px] tracking-[0.35em] uppercase text-[#c5a059] mb-2">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-serif-display text-xl mb-2">{r.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>

          <div className="rounded-xl p-5 border border-white/10 bg-white/[0.03] mt-10">
            <p className="text-xs text-white/60 leading-relaxed">
              <span className="text-[#c5a059] font-semibold">No investment is risk-free.</span>{" "}
              Universal Dream Properties LLC makes no representations of guaranteed returns,
              guaranteed rental income, guaranteed capital appreciation, or risk-free investment
              outcomes. All figures shown across this site are estimates based on current market
              conditions and are not a forecast of future performance.
            </p>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
